import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  authHeaders,
  useClassWithRoster,
  useGeneratePrintouts,
  usePrintouts,
} from "@/api/school";

/** 下载端点要求 Bearer;window.open 的裸浏览器导航不带任何头,线上
 *  直接回 401 JSON。改为带凭证 fetch 成 blob 再开新标签页。 */
export async function openAuthedPdf(url: string) {
  const r = await fetch(url, { headers: authHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const obj = URL.createObjectURL(await r.blob());
  window.open(obj, "_blank");
  setTimeout(() => URL.revokeObjectURL(obj), 60_000);
}

const REASONS = ["insert_student", "makeup_exam", "damaged", "version_change"] as const;

/** 记名卷 (P1-S3): whole-class merged PDF with preprinted name/code
 *  headers, single-student reprints with reasons, printout history. */
export function PrintoutsPanel({ examId, classId }: { examId: string; classId: string | null }) {
  const { t } = useTranslation();
  const printouts = usePrintouts(examId);
  const gen = useGeneratePrintouts(examId);
  const roster = useClassWithRoster(classId ?? undefined);
  const [allowMissing, setAllowMissing] = useState(false);
  const [reprintSel, setReprintSel] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState<(typeof REASONS)[number]>("makeup_exam");
  const [lastUrl, setLastUrl] = useState<string | null>(null);

  // detail 可能是对象(缺学号/已打印)也可能是一句话(如「班上没有学生」)—— 后者之前被吞掉,按钮点了没反应(2026-09-15 浏览器验收)。
  const err = gen.error as (Error & { detail?: string | { missing_student_codes?: string[]; already_printed?: string[]; hint?: string } }) | null;

  function run(studentIds?: string[], withReason?: boolean) {
    gen.mutate(
      {
        student_ids: studentIds,
        allow_missing_codes: allowMissing,
        reprint_reason: withReason ? reason : undefined,
      },
      {
        onSuccess: (r) => {
          setLastUrl(r.download);
          void openAuthedPdf(r.download);
          setReprintSel(new Set());
        },
      },
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-2">{t("printouts.title")}</h3>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={gen.isPending}
          onClick={() => run(undefined, (printouts.data?.printouts.length ?? 0) > 0)}
          className="px-4 py-2 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
        >
          {gen.isPending ? t("printouts.generating") : t("printouts.generateClass")}
        </button>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={allowMissing} onChange={(e) => setAllowMissing(e.target.checked)} />
          {t("printouts.allowMissing")}
        </label>
        {lastUrl && (
          <button
            type="button"
            onClick={() => void openAuthedPdf(lastUrl)}
            className="text-xs text-sky-600 hover:underline"
          >
            {t("printouts.downloadAgain")}
          </button>
        )}
      </div>

      {(typeof err?.detail === 'object' ? err?.detail?.missing_student_codes : undefined) && (
        <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
          {t("printouts.missingCodes")}: {(err?.detail as { missing_student_codes: string[] }).missing_student_codes.join("、")}
          <div className="mt-0.5">{t("printouts.missingHint")}</div>
        </div>
      )}
      {(typeof err?.detail === 'object' ? err?.detail?.already_printed : undefined) && (
        <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
          {t("printouts.alreadyPrinted")}: {(err?.detail as { already_printed: string[] }).already_printed.join("、")} — {t("printouts.pickReason")}
        </div>
      )}
      {gen.isError && !(typeof err?.detail === 'object' ? err?.detail?.missing_student_codes : undefined) && !(typeof err?.detail === 'object' ? err?.detail?.already_printed : undefined) && (
        <p className="mt-2 text-xs text-rose-500">
          {typeof err?.detail === "string" ? err.detail : (err?.detail as { hint?: string } | undefined)?.hint ?? String(gen.error)}
        </p>
      )}
      {gen.data && (
        <p className="mt-2 text-xs text-emerald-600">
          {t("printouts.done", { n: gen.data.students, v: gen.data.paper_version })}
          {gen.data.missing_codes.length > 0 && ` · ${t("printouts.blankCodes", { n: gen.data.missing_codes.length })}`}
        </p>
      )}

      {classId && (roster.data?.students?.length ?? 0) > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="text-xs font-semibold text-slate-500 mb-1.5">{t("printouts.reprintHeader")}</div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {roster.data!.students.map((s) => {
              const on = reprintSel.has(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    const next = new Set(reprintSel);
                    if (on) next.delete(s.id);
                    else next.add(s.id);
                    setReprintSel(next);
                  }}
                  className={`rounded border px-2 py-0.5 text-xs ${
                    on ? "bg-sky-500 border-sky-500 text-white" : "bg-white border-slate-200 text-slate-600"
                  }`}
                >
                  {s.name_zh}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as (typeof REASONS)[number])}
              className="rounded border border-slate-200 px-2 py-1 text-xs"
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>{t(`printouts.reasons.${r}`)}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={reprintSel.size === 0 || gen.isPending}
              onClick={() => run([...reprintSel], true)}
              className="px-3 py-1.5 text-xs rounded border border-sky-300 text-sky-600 hover:bg-sky-50 disabled:opacity-50"
            >
              {t("printouts.reprintSel", { n: reprintSel.size })}
            </button>
          </div>
        </div>
      )}

      {(printouts.data?.printouts.length ?? 0) > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-500">
          {printouts.data!.printouts.slice(0, 8).map((r, i) => (
            <div key={i} className="flex gap-2 py-0.5">
              <span>{r.name_zh}</span>
              <span className="font-mono">v{r.paper_version}</span>
              <span>{r.generated_at.slice(0, 16).replace("T", " ")}</span>
              {r.reprint_reason && <span className="text-amber-600">{t(`printouts.reasons.${r.reprint_reason}`)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
